/* eslint-disable import/no-unresolved */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';
import { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
    showReportMenuVar, leftNavigationItemsVar, moduleIdVar, currentLeftMenuVar,
    tenantIdVar,
} from 'Apollo/config';
import { GET_AUDIT_LOGS_FOR_FR2052A, GET_FAILED_TEST_RESULTS_FOR_FR2052A } from 'Apollo/Queries/Rules/FR2052aResults';
import crossReportImg from 'assets/images/cross-report-black.svg';
import { PageSizeOptionEnum, ModuleEnum } from 'constants';
import FR2052AResults from '../components/FR2052AResults';

const {
    FIVE, TEN, FIFTEEN, TWENTY, TWENTYFIVE,
} = PageSizeOptionEnum;

const listItems = [
    [
        <i key={1} className="appkiticon icon-bar-chart-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ fontSize: '0.7rem', color: '#464646' }}>Data Reconciliation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-ticket-outline" style={{ fontSize: '1.5rem', color: 'white', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ fontSize: '0.7rem' }}>Rule Engine</span>,
    ],
    [
        <img
            key={1}
            src={crossReportImg}
            alt="Cross Report"
            style={{
                width: '30.84px', height: '25px', top: '100px',
            }}
        />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Cross Report Validation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-grow-revenue-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Trend Analytics</span>,
    ],
];

const FR2052AResultsContainer = () => {
    const fr2052aPageSizeOptions = [FIVE, TEN, FIFTEEN, TWENTY, TWENTYFIVE];
    const location = useLocation();
    const history = useHistory();
    const [fr2052aBatchId, setBatchId] = useState(location.state.batchId);
    const [fr2052aRuleId, setRuleId] = useState(location.state.ruleId);
    const [data, setData] = useState([]);
    const [fr2052aPages, setPages] = useState(null);
    const [fr2052aPageSize, setPageSize] = useState(TEN);
    const [fr2052aPageNumber, setPageNumber] = useState(1);
    const tenantId = useReactiveVar(tenantIdVar);

    /* Apollo query to fetch audit logs */
    const auditLog = useQuery(GET_AUDIT_LOGS_FOR_FR2052A, {
        variables: {
            input: {
                batchId: Number(fr2052aBatchId),
                ruleId: Number(fr2052aRuleId),
                tenantId: Number(tenantId),
            },
        },
    });

    /* Apollo query to fetch failed test data */
    const [getFailedTestsData, { data: { getFailedTestResultsForFR2052A = {} } = {} }] = useLazyQuery(GET_FAILED_TEST_RESULTS_FOR_FR2052A, {
        variables: {
            input: {
                batchId: Number(fr2052aBatchId),
                ruleId: Number(fr2052aRuleId),
                tenantId: Number(tenantId),
                pageSize: fr2052aPageSize,
                pageNumber: fr2052aPageNumber,
            },
        },
        fetchPolicy: 'no-cache',
    });

    /* Calls getFailedTestsData() */
    const fetchData = useCallback(
        () => {
            getFailedTestsData();
        },
    );

    /* Method for side navigation */
    const onNavigate = () => {
        // sidebar navigation
    };

    /* Column configuration */
    const columns = [
        {
            Header: 'Row ID',
            accessor: 'rowId',
            minWidth: 200,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Error Message',
            accessor: 'errorMessage',
            minWidth: 170,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Test Date',
            accessor: 'testDate',
            minWidth: 120,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Test Fail',
            accessor: 'testFail',
            minWidth: 80,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Test Error',
            accessor: 'testError',
            minWidth: 100,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Test Run Status',
            accessor: 'testRunStatus',
            minWidth: 150,
            resizable: true,
            sortable: true,
        },
    ];

    /* Breadcrumb items configuration */
    const breadcrumbItems = [
        {
            link: '',
            value: (
                <span
                    className="appkiticon icon-home-fill font-16"
                    onClick={
                        () => {
                            showReportMenuVar(false);
                            history.push('/');
                            leftNavigationItemsVar([]);
                            moduleIdVar(null);
                        }
                    }
                    role="presentation"
                />
            ),
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text" onClick={() => { history.goBack(); }} role="presentation">FR 2052A Diagnostic Results</span>,
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text">{`Rule #${(fr2052aRuleId) || ''} Results`}</span>,
        },
    ];

    useEffect(() => {
        leftNavigationItemsVar(listItems);
        currentLeftMenuVar(ModuleEnum.RULEENGINE);
    }, []);

    /* Side effect when location state updates */
    useEffect(() => {
        setBatchId(location.state.batchId);
        setRuleId(location.state.ruleId);
        showReportMenuVar(true);
    }, [location]);

    /* Side effect when getFailedTestResultsForFR2052A changes */
    useEffect(() => {
        if (getFailedTestResultsForFR2052A && getFailedTestResultsForFR2052A.pagination !== null && Object.keys(getFailedTestResultsForFR2052A).length > 0) {
            const totalPages = Math.ceil(getFailedTestResultsForFR2052A.pagination.totalCount / fr2052aPageSize);
            setData(getFailedTestResultsForFR2052A.testResults);
            setPages(totalPages);
        }
    }, [getFailedTestResultsForFR2052A]);

    return (
        <FR2052AResults
            breadcrumbItems={breadcrumbItems}
            onNavigate={onNavigate}
            criteria={getFailedTestResultsForFR2052A && getFailedTestResultsForFR2052A.criteria}
            columns={columns}
            fetchData={fetchData}
            data={data}
            pages={fr2052aPages}
            setPageNumber={setPageNumber}
            pagesize={fr2052aPageSize}
            setPageSize={setPageSize}
            refetch={getFailedTestsData}
            auditLog={auditLog}
            pageSizeOptions={fr2052aPageSizeOptions}
        />
    );
};

export default FR2052AResultsContainer;
